import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, AlertTriangle } from 'lucide-react'; // Added AlertTriangle
import AuthCard from './AuthCard';
import AuthLayout from './AuthLayout'; // Import the new AuthLayout
import { Alert as ShadcnAlert, AlertDescription, AlertTitle } from '../components/ui/alert'; // Import Shadcn Alert

const useQuery = () => new URLSearchParams(useLocation().search);

export const ResetRequest = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [loginStatus, setLoginStatus] = useState(0);
  const [formData, setFormData] = useState({ email: '' });
  const [errors, setErrors] = useState({});
  // State for Shadcn Alert
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    const checkLoginStatus = () => {
      if (cookies['token']) {
        window.location.href = '/v2/home';
      }
    };
    if (loginStatus === 0) {
      checkLoginStatus();
    }
    return () => {};
  }, [loginStatus]);

  const validateEmail = (email) => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'email') {
      if (value.trim() === '') {
        setErrors((prev) => ({ ...prev, email: undefined })); // Clear error if empty
      } else if (!validateEmail(value)) {
        setErrors((prev) => ({ ...prev, email: 'Please enter a valid email' }));
      } else {
        setErrors((prev) => ({ ...prev, email: undefined })); // Clear error if valid
      }
    } else {
      // Clear other errors on input change if they exist
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedEmail = formData.email.trim();

    const newErrors = {};
    if (!trimmedEmail) {
      newErrors.email = 'This field is required';
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setShowAlert(false);
    setAlertMessage('');

    axios
      .post(`/api/auth/user/send-password`, {
        email: trimmedEmail,
      })
      .then((res) => {
        if (res?.status === 200) {
          setShowDetails(true);
          setLoading(false);
        } else {
          setAlertMessage('Ensure that you have entered the correct email');
          setShowAlert(true);
          setLoading(false);
        }
      })
      .catch((err) => {
        setAlertMessage('');
        setErrors(() => ({ email: 'No account found with this email.' }));
        setShowAlert(false);
        setLoading(false);
      });
  };

  const handleBack = () => {
    navigate('/auth/login');
  };

  if (showDetails) {
    return (
      <AuthLayout>
        <AuthCard
          title="Check Your Email"
          subtitle="The password reset link has been sent to your email. Please follow the instructions specified in the email."
          showBackButton={true}
          onBack={handleBack}
        >
          <Button
            onClick={handleBack}
            className={`w-full h-[40px] rounded-full text-base font-medium}`}
            style={{
              backgroundColor: '#4338ca',
              color: '#ffffff',
              border: 'none',
            }}
          >
            Back to Login
          </Button>
        </AuthCard>
      </AuthLayout>
    );
  }

  const isFormValid = formData.email && validateEmail(formData.email);

  return (
    <AuthLayout>
      <AuthCard
        title="Forgot Password"
        subtitle="Enter your registered email to get the password."
        showBackButton={true}
        onBack={handleBack}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" style={{ color: '#1d2933' }}>
              Email Address
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="off"
              placeholder="name@address.com"
              className={`h-[40px] rounded-full ${errors?.email ? 'border-[#f3164e]' : ''}`}
              style={{
                backgroundColor: '#ffffff',
                border: errors?.email ? '1px solid #f3164e' : '1px solid #d9e6f2',
                color: '#1d2933',
              }}
            />
            {errors?.email && (
              <div className="flex items-center gap-1 text-sm" style={{ color: '#f3164e' }}>
                <AlertTriangle size={16} className="text-[#f3164e]" />
                {errors.email}
              </div>
            )}
          </div>
          {showAlert && (
            <div className="flex gap-1 items-center justify-center text-sm text-center" style={{ color: '#f3164e' }}>
              <AlertTriangle size={25} className="text-[#f3164e]" />
              Error : {alertMessage}
            </div>
          )}

          <Button
            type="submit"
            disabled={!isFormValid}
            className={`w-full h-[40px] rounded-full text-base font-medium ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{
              backgroundColor: '#4338ca',
              color: '#ffffff',
              border: 'none',
            }}
          >
            {loading ? 'Sending Password...' : 'Send Password'}
          </Button>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export const ResetConfirm = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const [loginStatus, setLoginStatus] = useState(0);
  const [error, setError] = useState(false);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [passVisible, setPassVisible] = useState(false);
  const [passCnfVisible, setPassCnfVisible] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  // State for Shadcn Alert
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const token = query.get('token');

  useEffect(() => {
    const checkLoginStatus = () => {
      if (cookies['token']) {
        window.location.href = '/v2/home';
      }
    };
    if (loginStatus === 0) {
      checkLoginStatus();
    }
    return () => {};
  }, [loginStatus]);

  useEffect(() => {
    if (token && token.length > 0) {
      setLoading(true);
      axios
        .post(`/api/auth/user/resetPassVerify`, { token })
        .then((res) => {
          if (res?.status === 200) {
            setLoading(false);
          } else {
            setError(true);
            setLoading(false);
          }
        })
        .catch((err) => {
          setError(true);
          setLoading(false);
        });
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'password') {
      if (value.trim() === '') {
        setErrors((prev) => ({ ...prev, password: undefined }));
      } else if (value.length < 6) {
        setErrors((prev) => ({ ...prev, password: 'Password must be at least 6 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, password: undefined }));
      }
      // Also validate confirm password if it's currently filled to ensure they match
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords must match' }));
      } else if (formData.confirmPassword && value === formData.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
      }
    } else if (name === 'confirmPassword') {
      if (value.trim() === '') {
        setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
      } else if (formData.password !== value) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords must match' }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
      }
    } else {
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm Password is required';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords must match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedPassword = formData.password.trim(); // Trim whitespace
    const trimmedConfirmPassword = formData.confirmPassword.trim(); // Trim whitespace

    // Update formData with trimmed values before validation
    setFormData((prev) => ({
      ...prev,
      password: trimmedPassword,
      confirmPassword: trimmedConfirmPassword,
    }));

    const newErrors = {};

    if (!trimmedPassword) {
      // Validate trimmed password
      newErrors.password = 'Password is required';
    } else if (trimmedPassword.length < 6) {
      // Validate trimmed password length
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!trimmedConfirmPassword) {
      // Validate trimmed confirm password
      newErrors.confirmPassword = 'Confirm Password is required';
    } else if (trimmedPassword !== trimmedConfirmPassword) {
      newErrors.confirmPassword = 'Passwords must match';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setFormLoading(true);
    setShowAlert(false);
    setAlertMessage('');

    axios
      .patch(`/api/auth/user/resetPassConfirm`, {
        token,
        newPassword: trimmedPassword,
      })
      .then((res) => {
        setFinished(true);
      })
      .catch((err) => {
        setError(true);
        setAlertMessage('An error occurred while setting your new password. Please try again.'); // Specific alert message for confirm password failure
        setShowAlert(true);
        setFormLoading(false);
      });
  };
  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  if (loading) {
    return <>Loading...</>;
  }

  if (error) {
    return (
      <AuthLayout>
        <AuthCard
          title="Reset Password Error"
          subtitle="An error occurred while resetting your password. Your password has not been changed. Please check your email for a new reset password link or contact us."
          showBackButton={true}
          onBack={handleBackToLogin}
        >
          <div className="text-center mt-8">
            <p className="text-sm" style={{ color: '#60748f' }}>
              Having trouble?
              <br />
              <a
                href="mailto:support@mathongo.com?subject=I cannot login to Quizrr"
                className="underline hover:no-underline"
                style={{ color: '#4338ca' }}
              >
                Contact us
              </a>
            </p>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  if (finished) {
    return (
      <AuthLayout>
        <AuthCard
          title="Password Changed"
          subtitle="Your password has been successfully changed."
          showBackButton={true}
          onBack={handleBackToLogin}
        >
          <div className="space-y-6">
            <Button
              onClick={handleBackToLogin}
              className="w-full h-[40px] text-base font-medium"
              style={{
                backgroundColor: '#4338ca',
                color: '#ffffff',
                border: 'none',
              }}
            >
              Back to Login
            </Button>

            <div className="text-center">
              <p className="text-sm" style={{ color: '#60748f' }}>
                You can log in to your account from{' '}
                <button
                  onClick={handleBackToLogin}
                  className="underline hover:no-underline"
                  style={{ color: '#4338ca' }}
                >
                  here
                </button>
              </p>
            </div>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  const isFormValid =
    formData.password &&
    formData.confirmPassword &&
    formData.password === formData.confirmPassword &&
    formData.password.length >= 6;

  return (
    <AuthLayout>
      <AuthCard
        title="Password Sent on Email"
        subtitle="The password has been sent to your email. Please follow the instructions specified in the email."
        showBackButton={true}
        onBack={handleBackToLogin}
      >
        {/* <form onSubmit={handleSubmit} className="space-y-6">
                    {showAlert && (
                        <ShadcnAlert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertTitle>Error</AlertTitle>
                            <AlertDescription>
                                {alertMessage}
                            </AlertDescription>
                        </ShadcnAlert>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="password" style={{ color: "#1d2933" }}>
                            Password
                        </Label>
                        <div className="relative">
                            <Input
                                id="password"
                                name="password"
                                type={passVisible ? "text" : "password"}
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter your password"
                                className={`h-[40px] rounded-full pr-12 ${errors?.password ? "border-[#f3164e]" : ""}`}
                                style={{
                                    backgroundColor: "#ffffff",
                                    border: errors?.password ? "1px solid #f3164e" : "1px solid #d9e6f2",
                                    color: "#1d2933",
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setPassVisible(!passVisible)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                                style={{ color: "#60748f" }}
                            >
                                {passVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {errors?.password && (
                            <div className="flex items-center gap-1 text-sm" style={{ color: "#f3164e" }}>
                                <AlertTriangle size={16} className="text-[#f3164e]" />
                                {errors.password}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirmPassword" style={{ color: "#1d2933" }}>
                            Confirm Password
                        </Label>
                        <div className="relative">
                            <Input
                                id="confirmPassword"
                                name="confirmPassword"
                                type={passCnfVisible ? "text" : "password"}
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Enter your password again"
                                className={`h-[40px] rounded-full pr-12 ${errors?.confirmPassword ? "border-[#f3164e]" : ""}`}
                                style={{
                                    backgroundColor: "#ffffff",
                                    border: errors?.confirmPassword
                                        ? "1px solid #f3164e"
                                        : "1px solid #d9e6f2",
                                    color: "#1d2933",
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setPassCnfVisible(!passCnfVisible)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                                style={{ color: "#60748f" }}
                            >
                                {passCnfVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {errors?.confirmPassword && (
                            <div className="flex items-center gap-1 text-sm" style={{ color: "#f3164e" }}>
                                <AlertTriangle size={16} className="text-[#f3164e]" />
                                {errors.confirmPassword}
                            </div>
                        )}
                    </div>

                    {formLoading ? (
                        <div className="text-center">
                            <div
                                className="inline-block animate-spin rounded-full h-8 w-8 border-b-2"
                                style={{ borderColor: "#4338ca" }}
                            ></div>
                        </div>
                    ) : (
                        <Button
                            type="submit"
                            disabled={!isFormValid}
                            className={`w-full rounded-full h-[40px] text-base font-medium ${!isFormValid ? "opacity-50 cursor-not-allowed" : ""}`}
                            style={{
                                backgroundColor: "#4338ca",
                                color: "#ffffff",
                                border: "none",
                            }}
                        >
                            Set New Password
                        </Button>
                    )}
                </form> */}
        <Button
          onClick={handleBackToLogin}
          className={`w-full rounded-full h-[40px] text-base`}
          style={{
            backgroundColor: '#4338ca',
            color: '#ffffff',
            border: 'none',
          }}
        >
          Back To Login
        </Button>
      </AuthCard>
    </AuthLayout>
  );
};
